import React from 'react';
import { Input, Button } from 'reactstrap';
import { MultiSelect } from 'react-multi-select-component';
import { FOB_MULTI_SELECT_STRINGS } from './constants';
import { getMultiSelectValue } from './utils';

export const CounterProductAlert = ({ form }) => (
  <div className='alert alert-primary bg-primary-subtle border-0 text-primary p-3 d-flex gap-2 rounded-3 mb-4 align-items-start'>
    <i className='ri-information-line fs-18 mt-0'></i>
    <div>
      <h6 className='fw-semibold mb-1 text-primary fs-14'>
        Alternative Requirement Offering
      </h6>
      <p className='small text-muted mb-0 fs-12'>
        Propose alternative delivery terms. Product must remain:{' '}
        <span className='fw-bold text-primary'>
          {[form.productName, form.productTypename].filter(Boolean).join(' ') ||
            form.productType?.name ||
            '-'}
        </span>
      </p>
    </div>
  </div>
);

export const CounterModalLoading = () => (
  <div className='text-center py-5'>
    <div className='spinner-border text-primary' role='status'>
      <span className='visually-hidden'>Loading...</span>
    </div>
  </div>
);

export const CounterFieldError = ({ message }) =>
  message ? <div className='invalid-feedback d-block'>{message}</div> : null;

export const CounterModalFooter = ({
  loading,
  saveDisabled = false,
  onCancel,
  onSave,
  formData,
}) => (
  <div className='border-top p-3 bg-white d-flex justify-content-end gap-2'>
    <Button
      color='light'
      onClick={onCancel}
      className='px-4 py-2 fw-semibold'
      disabled={loading}
    >
      Cancel
    </Button>
    <Button
      color='primary'
      onClick={() => onSave(formData)}
      className='px-4 py-2 fw-semibold'
      disabled={loading || saveDisabled}
      title={saveDisabled ? 'Counter data must differ from original data' : ''}
    >
      Save
    </Button>
  </div>
);

export const CounterDeliveryBasisFields = ({ formData, onFormChange }) => (
  <div className='row g-3 mb-3'>
    <div className='col-md-6'>
      <div className='form-group'>
        <label className='fw-semibold text-muted fs-13 mb-1'>
          Delivery Basis
        </label>
        <Input
          type='text'
          value={formData.deliveryBasis}
          onChange={e => onFormChange('deliveryBasis', e.target.value)}
          className='py-2 text-dark fw-medium'
        />
      </div>
    </div>
  </div>
);

export const useLocationBlockRenderer = ({
  incotermLocationOptions,
  onFormChange,
  errors = {},
}) => {
  const handleFobMultiSelectChange = (field, selectedItems) => {
    onFormChange(field, selectedItems?.map(item => item.value) || []);
  };

  const LocationBlockRenderer = ({
    label,
    locationValue,
    locationField,
    multiSelectField,
    dateFromValue,
    dateFromField,
    dateToValue,
    dateToField,
    quantityValue,
    quantityField,
    showQuantity,
    isMultiSelect,
    multiSelectValue,
  }) => (
    <div className='p-3 bg-light rounded-3 border-0 h-100'>
      <div className='form-group mb-3'>
        <label className='fw-semibold text-muted fs-13 mb-1'>{label}</label>
        {isMultiSelect ? (
          <MultiSelect
            options={incotermLocationOptions}
            value={getMultiSelectValue(
              incotermLocationOptions,
              multiSelectValue
            )}
            onChange={selectedItems =>
              handleFobMultiSelectChange(
                multiSelectField || locationField,
                selectedItems
              )
            }
            labelledBy='Please select'
            overrideStrings={FOB_MULTI_SELECT_STRINGS}
            hasSelectAll
            disableSearch={false}
            className='counter-fob-multiselect'
          />
        ) : (
          <Input
            type='select'
            value={locationValue}
            onChange={e => onFormChange(locationField, e.target.value)}
            className='py-2 form-select text-muted'
            invalid={!!errors[locationField]}
          >
            <option value=''>Please select</option>
            {incotermLocationOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        )}
        <CounterFieldError
          message={errors[multiSelectField || locationField]}
        />
      </div>
      {showQuantity && (
        <div className='form-group mb-3'>
          <label className='fw-semibold text-muted fs-13 mb-1'>Quantity</label>
          <Input
            type='text'
            placeholder='Input Quantity'
            value={quantityValue}
            onChange={e => onFormChange(quantityField, e.target.value)}
            className='py-2'
            invalid={!!errors[quantityField]}
          />
          <CounterFieldError message={errors[quantityField]} />
        </div>
      )}
      <div className='form-group mb-3'>
        <label className='fw-semibold text-muted fs-13 mb-1'>Date From</label>
        <Input
          type='date'
          value={dateFromValue}
          onChange={e => onFormChange(dateFromField, e.target.value)}
          className='py-2 text-muted'
          invalid={!!errors[dateFromField]}
        />
        <CounterFieldError message={errors[dateFromField]} />
      </div>
      <div className='form-group mb-0'>
        <label className='fw-semibold text-muted fs-13 mb-1'>Date To</label>
        <Input
          type='date'
          value={dateToValue}
          onChange={e => onFormChange(dateToField, e.target.value)}
          className='py-2 text-muted'
          invalid={!!errors[dateToField]}
        />
        <CounterFieldError message={errors[dateToField]} />
      </div>
    </div>
  );
  LocationBlockRenderer.displayName = 'LocationBlockRenderer';
  return LocationBlockRenderer;
};
